import { BlocklyHelperService } from "../blockly-helper.service";
import { BLOCKLY_COLORS, BLOCKLY_DUMMY, BlocklyToolboxCategory } from "../blocky-types.types";

/**
 * Kategorie "Vor dem Senden" – bildet RAGAI.BEFORE_SEND.add() als Blockly-Pipe ab.
 *
 * RAGAI.BEFORE_SEND.add(fn) registriert eine Funktion, die bei jeder Nutzereingabe
 * ausgeführt wird, bevor die Nachricht an die KI gesendet wird. `fn` bekommt die
 * aktuelle Nachricht als Parameter und gibt entweder die (ggf. veränderte) Nachricht
 * oder `null` zurück. Gibt eine Funktion `null` zurück, wird das Senden abgebrochen.
 *
 * Diese Kategorie erlaubt es, so eine Funktion visuell zusammenzubauen:
 * - BEFORE_SEND_ADD:        Wrapper-Block (Hat-Block), erzeugt RAGAI.BEFORE_SEND.add(...)
 * - BEFORE_SEND_GET_MESSAGE Value-Block, liest die aktuelle Nachricht
 * - BEFORE_SEND_SET_MESSAGE Statement-Block, überschreibt die aktuelle Nachricht
 * - BEFORE_SEND_ABORT       Statement-Block, bricht das Senden endgültig ab
 * - BEFORE_SEND_ABORT_IF    Statement-Block, bricht das Senden nur bedingt ab
 */
export function BlocklyCategory_BEFORE_SEND(name: string = "unnamed", BH: BlocklyHelperService): BlocklyToolboxCategory {
  const cat: BlocklyToolboxCategory = { kind: 'category', colour: BLOCKLY_COLORS.BEFORE_SEND, name: name, contents: [] };

  const contents: NonNullable<BlocklyToolboxCategory['contents']> = [];

  contents.push(BH.label(name));

  contents.push(
    BH._custom(
      "BEFORE_SEND_ADD",
      "📨 Vor dem Senden an die KI:\nNachricht bearbeiten\n{{action:statement[@check=ACTION]}}",
      `RAGAI.BEFORE_SEND.add(async function(message){
{{action}}
return message;
});`,
      {
        colour: BLOCKLY_COLORS.BEFORE_SEND,
        hat: 'cap',
        connection: 'none',
        inputsInline: true,
        tooltip: "Wird bei jeder Nutzereingabe ausgeführt, bevor sie an die KI gesendet wird.\nNutze '💬 aktuelle Nachricht' zum Lesen, '✏️ Nachricht ändern zu' zum Überschreiben oder '🚫 Senden abbrechen', um das Senden zu verhindern.\nDu kannst mehrere dieser Blöcke anlegen – sie werden der Reihe nach wie eine Pipe ausgeführt.\n⚠️ Verwende hier KEINE Blöcke, die selbst eine Nachricht an den Bot senden (z.B. 'Prompt senden', 'Nutzer Nachricht') – das würde diesen Block erneut auslösen."
      }
    )
  );

  contents.push(
    BH._custom(
      "BEFORE_SEND_GET_MESSAGE",
      "@output:String\n💬 aktuelle Nachricht",
      "message",
      {
        colour: BLOCKLY_COLORS.BEFORE_SEND_LIGHT,
        tooltip: "Liefert die Nachricht, wie sie aktuell in der Verarbeitungskette aussieht (inkl. Änderungen durch vorherige Blöcke). Nur innerhalb von 'Vor dem Senden an die KI' verwendbar."
      }
    )
  );

  contents.push(
    BH.withNested(
      BH._custom(
        "BEFORE_SEND_SET_MESSAGE",
        "✏️ Nachricht ändern zu: {{value[@check=String]}}",
        "message = {{value}};",
        {
          colour: BLOCKLY_COLORS.BEFORE_SEND,
          previousStatement: 'ACTION',
          nextStatement: 'ACTION',
          inputsInline: true,
          tooltip: "Überschreibt die Nachricht mit einem neuen Text. Nachfolgende Blöcke arbeiten mit dem neuen Text weiter."
        }
      ),
      [{ forValue: "value", isShadow: true, targetBlockType: BLOCKLY_DUMMY.STRING, targetBlockDefaults: { text: "" } }]
    )
  );

  contents.push(
    BH._custom(
      "BEFORE_SEND_ABORT",
      "🚫 Senden abbrechen (Nachricht wird NICHT an die KI gesendet)",
      "return null;",
      {
        colour: BLOCKLY_COLORS.ACTIONS,
        previousStatement: 'ACTION',
        connection: 'top',
        tooltip: "Bricht die Verarbeitung endgültig ab: Die Nachricht wird nicht an die KI gesendet. Blöcke danach werden nicht mehr ausgeführt."
      }
    )
  );

  contents.push(
    BH._custom(
      "BEFORE_SEND_ABORT_IF",
      "🚫 Wenn {{condition[@check=Boolean]}} dann: Senden abbrechen",
      "if({{condition}}) return null;",
      {
        colour: BLOCKLY_COLORS.ACTIONS,
        previousStatement: 'ACTION',
        nextStatement: 'ACTION',
        inputsInline: true,
        tooltip: "Bricht das Senden nur ab, wenn die Bedingung erfüllt ist. Ist die Bedingung falsch, laufen die nachfolgenden Blöcke normal weiter."
      }
    )
  );

  cat.contents = contents;
  return cat;
}
